import { useSyncExternalStore } from "react";
const PREF_KEY = "midnight-music";
const GESTURES = ["pointerdown", "touchstart", "keydown", "scroll", "wheel", "click"];
function readPref() {
  try {
    const v = window.sessionStorage.getItem(PREF_KEY);
    return v === "off" ? "off" : v === "on" ? "on" : null;
  } catch {
    return null;
  }
}
function writePref(v) {
  try {
    window.sessionStorage.setItem(PREF_KEY, v);
  } catch {
  }
}
let audio = null;
let pref = null;
let attempted = false;
let detachGesture = null;
let snapshot = {
  tracks: [],
  index: 0,
  playing: false,
  blocked: false,
  current: 0,
  duration: 0,
  muted: false,
  volume: 0.85,
  error: null
};
const listeners = /* @__PURE__ */ new Set();
function emit(patch) {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((l) => l());
}
function currentTrack() {
  return snapshot.tracks[snapshot.index];
}
function ensureAudio() {
  if (typeof window === "undefined") return null;
  if (audio) return audio;
  const el = new window.Audio();
  el.preload = "auto";
  el.loop = true;
  el.volume = snapshot.volume;
  el.muted = snapshot.muted;
  el.addEventListener("play", () => emit({ playing: true, error: null }));
  el.addEventListener("playing", () => emit({ playing: true, blocked: false }));
  el.addEventListener("pause", () => emit({ playing: false }));
  el.addEventListener("ended", () => emit({ playing: false }));
  el.addEventListener("timeupdate", () => emit({ current: el.currentTime }));
  el.addEventListener("loadedmetadata", () => {
    if (Number.isFinite(el.duration) && el.duration > 0) emit({ duration: el.duration });
  });
  el.addEventListener("volumechange", () => emit({ muted: el.muted, volume: el.volume }));
  el.addEventListener(
    "error",
    () => emit({
      playing: false,
      error: "This audio could not be played. It may still be uploading, or the file may be damaged."
    })
  );
  audio = el;
  if (typeof document !== "undefined" && document.body) {
    el.style.display = "none";
    el.setAttribute("data-music-engine", "");
    document.body.appendChild(el);
  }
  return el;
}
function applySource() {
  const el = ensureAudio();
  const track = currentTrack();
  if (!el || !track) return;
  el.src = track.url;
  el.volume = snapshot.volume;
  el.muted = snapshot.muted;
  emit({ current: 0, duration: track.durationSec ?? 0, error: null });
}
function clearGesture() {
  detachGesture?.();
  detachGesture = null;
}
function armGesture() {
  if (detachGesture || typeof document === "undefined") return;
  const handler = () => {
    clearGesture();
    if (pref === "off") return;
    void start();
  };
  GESTURES.forEach(
    (type) => document.addEventListener(type, handler, { once: true, passive: true, capture: true })
  );
  detachGesture = () => {
    GESTURES.forEach(
      (type) => document.removeEventListener(type, handler, { capture: true })
    );
  };
}
async function start() {
  const el = ensureAudio();
  if (!el || !el.src) return false;
  if (pref === "off") return false;
  try {
    await el.play();
    clearGesture();
    emit({ blocked: false, error: null });
    return true;
  } catch (err) {
    const name = err?.name ?? "";
    if (name === "NotAllowedError" || name === "AbortError") {
      if (name === "NotAllowedError") {
        emit({ playing: false, blocked: true });
        armGesture();
      }
      return false;
    }
    emit({ playing: false, error: "This audio could not be played just now." });
    return false;
  }
}
function setTracks(tracks, opts = {}) {
  if (!tracks.length) return;
  const same = snapshot.tracks.length === tracks.length && snapshot.tracks.every((t, i) => t.id === tracks[i].id && t.url === tracks[i].url);
  if (!same) {
    const index = Math.min(snapshot.index, tracks.length - 1);
    snapshot = { ...snapshot, tracks, index };
    applySource();
  }
  if (!attempted && opts.autoStart !== false) {
    attempted = true;
    if (pref === null) pref = readPref();
    if (pref === "off") return;
    const el = ensureAudio();
    if (el && !el.src) applySource();
    void start();
  }
}
function requestAutoplay() {
  if (pref === null) pref = readPref();
  if (pref === "off") return;
  const el = ensureAudio();
  if (el && !el.src) applySource();
  void start();
}
async function play() {
  pref = "on";
  writePref("on");
  emit({ blocked: false });
  return start();
}
function pause() {
  audio?.pause();
  emit({ playing: false });
}
async function toggle() {
  if (snapshot.playing) {
    pref = "off";
    writePref("off");
    clearGesture();
    audio?.pause();
    emit({ blocked: false });
    return false;
  }
  return play();
}
function seek(value) {
  if (!audio) return;
  audio.currentTime = value;
  emit({ current: value });
}
function setVolume(value) {
  const el = ensureAudio();
  if (!el) return;
  el.volume = value;
  if (value > 0 && el.muted) el.muted = false;
}
function setMuted(value) {
  const el = ensureAudio();
  if (!el) return;
  el.muted = value;
}
function nextTrack() {
  if (snapshot.tracks.length < 2) return;
  const wasPlaying = snapshot.playing;
  const index = (snapshot.index + 1) % snapshot.tracks.length;
  snapshot = { ...snapshot, index };
  applySource();
  if (wasPlaying) void start();
}
function subscribe(cb) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function getSnapshot() {
  return snapshot;
}
function useMusic() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
function useMusicSelector(select) {
  const read = () => select(snapshot);
  return useSyncExternalStore(subscribe, read, read);
}
function toTracks(rows) {
  return (rows ?? []).map((r) => ({
    id: r.id,
    url: r.url,
    caption: r.caption ?? null,
    mime: r.mime ?? null,
    durationSec: r.durationSec ?? null
  }));
}
export {
  nextTrack,
  pause,
  play,
  requestAutoplay,
  seek,
  setMuted,
  setTracks,
  setVolume,
  toTracks,
  toggle,
  useMusic,
  useMusicSelector
};
